import React, { useRef, useEffect } from "react";
import {
  FaInstagram,
  FaDiscord,
  FaTwitter,
  FaTwitch,
  FaYoutube,
  FaTiktok,
  FaPlay,
  FaPause,
  FaBackward,
  FaForward,
  FaMusic,
  FaVolumeUp,
  FaVolumeMute,
  FaSteam,
} from "react-icons/fa";
import logo from "../assets/logo.png";
import { useContent } from "../hooks/useContent";
import { motion, useMotionValue, useTransform } from "framer-motion";
import { usePlayer } from "../context/MusicPlayerContext";

const Hero: React.FC = () => {
  const { content } = useContent();

  const x = useMotionValue(0);
  const y = useMotionValue(0);

  const rotateX = useTransform(y, [-1, 1], [15, -15]);
  const rotateY = useTransform(x, [-1, 1], [-15, 15]);

  const ref = useRef<HTMLDivElement>(null);

  // Helper: map motion values into depth movement
  const depth = (multiplier: number) => {
    const moveX = useTransform(x, [-1, 1], [-25 * multiplier, 25 * multiplier]);
    const moveY = useTransform(y, [-1, 1], [-25 * multiplier, 25 * multiplier]);
    return { x: moveX, y: moveY };
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const { innerWidth, innerHeight } = window;
      const posX = (e.clientX / innerWidth) * 2 - 1; // -1 → 1
      const posY = (e.clientY / innerHeight) * 2 - 1; // -1 → 1
      x.set(posX);
      y.set(posY);
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, [x, y]);

  const getIconComponent = (iconName: string) => {
    const iconMap: { [key: string]: React.ReactNode } = {
      faceit: (
        <img
          src="/fafaceit.webp"
          alt="Faceit"
          className="w-8 h-8 object-contain transition-all duration-200"
          style={{ filter: "brightness(0) invert(1)" }}
          onMouseOver={(e) =>
            (e.currentTarget.style.filter =
              "brightness(0) saturate(100%) sepia(100%) hue-rotate(-20deg) brightness(1.2) invert(16%) sepia(99%) saturate(7492%) hue-rotate(-7deg) brightness(1.01) contrast(119%)")
          }
          onMouseOut={(e) =>
            (e.currentTarget.style.filter = "brightness(0) invert(1)")
          }
        />
      ),
      steam: <FaSteam />,
      instagram: <FaInstagram />,
      discord: <FaDiscord />,
      twitter: <FaTwitter />,
      twitch: <FaTwitch />,
      youtube: <FaYoutube />,
      tiktok: <FaTiktok />,
    };
    return iconMap[iconName] || <FaSteam />;
  };

  const {
  isPlaying, currentTime, duration, volume, muted,
  togglePlay, handleSeek, handleBackward, handleForward,
  handleVolumeChange, toggleMute,
} = usePlayer();

  const formatTime = (time: number) => {
    if (isNaN(time)) return "0:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60)
      .toString()
      .padStart(2, "0");
    return `${minutes}:${seconds}`;
  };

  return (
    <div className="min-h-screen text-white flex flex-col relative overflow-hidden">
      {/* Content overlay */}
      <div className="relative z-10 flex flex-col flex-1">
        {/* Navigation bar placeholder (should be global) */}

        {/* ── MOBILE layout (hidden on lg+) ── */}
        <div className="flex lg:hidden flex-col items-center px-4 py-8 gap-6">

          {/* Mobile card — full width, no fixed px sizes, no translate */}
          <motion.div
            className="
              w-full max-w-[420px]
              p-4
              flex flex-col items-center justify-center
              rounded-2xl border-4 border-[#ff2a2a]
              shadow-[0_0_40px_#ff2a2aaa]
              bg-white/5 backdrop-blur
            "
            style={{ rotateX, rotateY }}
            transition={{ type: "spring", stiffness: 120, damping: 20 }}
          >
            <motion.img
              src={logo}
              alt="Profile"
              className="w-20 h-20 rounded-full object-cover border-4 border-white mb-4 shadow-lg"
              style={{ boxShadow: "0 0 40px 0 #ff0000aa" }}
            />
            <span className="text-2xl font-extrabold text-[#ff2a2a] tracking-wider mb-3">
              150k
            </span>
            <div className="flex flex-wrap items-center justify-center gap-2 text-[#ff2a2a] text-sm mb-4">
              <span>Mika</span>
              <span>●</span>
              <span>25</span>
              <span>●</span>
              <span>Switzerland</span>
            </div>

            {/* Music Player */}
            <div className="w-full flex flex-col items-start bg-[#ff2a2a]/10 rounded-lg p-3">
              <div className="flex items-center mb-2">
                <span className="text-2xl text-[#ff2a2a] mr-3"><FaMusic /></span>
                <div>
                  <div className="text-[#ff2a2a] font-bold text-base">Dreams</div>
                  <div className="text-[#ff2a2a] text-xs">{formatTime(currentTime)}</div>
                </div>
              </div>
              <div className="w-full flex items-center space-x-2 mb-3">
                <div
  className="flex-1 h-1 bg-[#ff2a2a]/40 rounded-full overflow-hidden cursor-pointer"
  onClick={(e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    handleSeek(percent);
  }}
>
                  <div className="h-1 bg-[#ff2a2a] rounded-full" style={{ width: duration ? `${(currentTime / duration) * 100}%` : "0%" }} />
                </div>
                <span className="text-[#ff2a2a] text-xs">{formatTime(duration)}</span>
              </div>
              <div className="flex items-center gap-3">
                <button type="button" className="text-[#ff2a2a] text-xl hover:scale-125 transition-transform focus:outline-none" onClick={handleBackward}><FaBackward /></button>
                <button type="button" className="text-[#ff2a2a] text-2xl hover:scale-125 transition-transform focus:outline-none" onClick={togglePlay}>{isPlaying ? <FaPause /> : <FaPlay />}</button>
                <button type="button" className="text-[#ff2a2a] text-xl hover:scale-125 transition-transform focus:outline-none" onClick={handleForward}><FaForward /></button>
                <div className="flex items-center ml-2">
                  <button type="button" className="text-[#ff2a2a] text-xl mr-2 focus:outline-none" onClick={toggleMute}>
                    {muted || volume === 0 ? <FaVolumeMute /> : <FaVolumeUp />}
                  </button>
                  <input type="range" min="0" max="1" step="0.01" value={volume} onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
                    className="w-16 h-1 accent-[#ff2a2a] bg-[#ff2a2a]/30 rounded-lg outline-none" />
                </div>
              </div>
            </div>
          </motion.div>

          {/* Mobile right panel */}
          <div className="w-full max-w-[420px] flex flex-col items-center">
            {/* <a href="https://farmskins.com/ref-150k" target="_blank" rel="noopener noreferrer" className="z-[999] w-full flex justify-center mb-4">
              <video src="/overlay.webm" autoPlay loop muted playsInline className="w-full max-w-[280px] cursor-pointer" />
            </a> */}
            <h2 className="text-2xl font-bold mb-4 tracking-wider">SOCIAL MEDIA</h2>
            <div className="flex flex-wrap justify-center gap-4 mb-6 text-3xl">
              {content.socialLinks.map((s) => (
                <a key={s.id} href={s.link} target="_blank" rel="noopener noreferrer"
                  className="hover:text-red-500 transition-colors cursor-pointer" title={s.label}>
                  {getIconComponent(s.icon)}
                </a>
              ))}
            </div>
            <div className="w-full grid grid-cols-2 gap-2 text-xs mb-4">
              {content.systemSpecs.map((spec) => (
                <div key={spec.id} className="flex justify-between px-2 py-1">
                  <span className="text-red-500 font-semibold">{spec.label}</span>
                  <span className="text-white text-right">{spec.value}</span>
                </div>
              ))}
            </div>
            <div className="w-full border-t border-[#3a2d5c] pt-4">
              <h3 className="text-base font-bold text-white mb-2">CS2 SETTINGS</h3>
              <div className="flex flex-wrap gap-3 mb-2">
                {content.cs2Settings.map((s) => (
                  <div key={s.id} className="flex flex-col items-center">
                    <span className="text-red-500 font-bold text-sm">{s.value}</span>
                    <span className="text-xs text-white">{s.label}</span>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-3 mb-2">
                {content.viewmodel.map((s) => (
                  <div key={s.id} className="flex flex-col items-center">
                    <span className="text-red-500 font-bold text-sm">{s.value}</span>
                    <span className="text-xs text-white">{s.label}</span>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-3 mb-4">
                {content.videoSettings.map((s) => (
                  <div key={s.id} className="flex flex-col items-center">
                    <span className="text-red-500 font-bold text-sm">{s.value}</span>
                    <span className="text-xs text-white">{s.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ── DESKTOP layout (original, completely untouched, hidden on mobile) ── */}
        <main className="hidden lg:flex flex-1 flex-col items-center justify-center px-2">
          <div className="w-full max-w-7xl grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Panel - Profile, Socials, Music Player */}
            <div className="flex flex-col items-center justify-center justify-self-start transform -translate-x-52">
              <div className="tilt-container">
                <motion.div
                  ref={ref}
                  className="
                    w-[320px] h-[220px]
                    sm:w-[420px] sm:h-[280px]
                    md:w-[560px] md:h-[360px]
                    lg:w-[720px] lg:h-[440px]
                    xl:w-[900px] xl:h-[500px]
                    p-4 sm:p-5 md:p-6 lg:p-7 xl:p-8
                    flex flex-col items-center justify-center
                    mb-16 relative overflow-hidden
                    rounded-2xl border-4 border-[#ff2a2a]
                    shadow-[0_0_40px_#ff2a2aaa]
                    bg-white/5 backdrop-blur
                  "
                  style={{ rotateX, rotateY }}
                  transition={{ type: "spring", stiffness: 120, damping: 20 }}
                >
                  {/* Profile Image with depth */}
                  <motion.img
                    src={logo}
                    alt="Profile"
                    className="w-32 h-32 rounded-full object-cover border-4 border-white mb-4 shadow-lg"
                    style={{ ...depth(1.2), boxShadow: "0 0 40px 0 #ff0000aa" }}
                  />

                  {/* Username */}
                  <motion.div
                    className="flex items-center justify-center w-full mb-4"
                    style={{ ...depth(0.8) }}
                  >
                    <span className="text-4xl font-extrabold text-[#ff2a2a] tracking-wider">
                      150k
                    </span>
                  </motion.div>

                  {/* Location + viewers */}
                  <motion.div
                    className="flex items-center justify-center space-x-4 text-[#ff2a2a] mb-8 w-full"
                    style={{ ...depth(0.6) }}
                  >
                    <span className="flex items-center space-x-1">
                      <i className="fa-solid fa-eye"></i>
                      <span>Mika</span>
                    </span>
                    <span className="text-[#ff2a2a] text-xl">●</span>
                    <span className="flex items-center space-x-1">
                      <i className="fa-solid fa-eye"></i>
                      <span>25</span>
                    </span>
                    <span className="text-[#ff2a2a] text-xl">●</span>
                    <span className="flex items-center space-x-1">
                      <i className="fa-solid fa-location-dot"></i>
                      <span>Switzerland</span>
                    </span>
                  </motion.div>

                  {/* Music Player */}
                  <motion.div
                    className="w-full flex flex-col items-start bg-[#ff2a2a]/10 rounded-lg p-4"
                    style={{ ...depth(0.4) }}
                  >
                    <div className="flex items-center mb-2">
                      <span className="text-3xl text-[#ff2a2a] mr-3">
                        <FaMusic />
                      </span>
                      <div>
                        <div className="text-[#ff2a2a] font-bold text-lg">
                          Dreams
                        </div>
                        <div className="text-[#ff2a2a] text-xs">
                          {formatTime(currentTime)}
                        </div>
                      </div>
                    </div>
                    <div className="w-full flex items-center space-x-2">
                        <div
  className="flex-1 h-1 bg-[#ff2a2a]/40 rounded-full overflow-hidden cursor-pointer"
  onClick={(e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    handleSeek(percent);
  }}
>
                        <div
                          className="h-1 bg-[#ff2a2a] rounded-full"
                          style={{
                            width: duration
                              ? `${(currentTime / duration) * 100}%`
                              : "0%",
                          }}
                        ></div>
                      </div>
                      <span className="text-[#ff2a2a] text-xs ml-2">
                        {formatTime(duration)}
                      </span>
                    </div>
                    <div className="flex items-center space-x-4 mt-3">
                      <button
                        type="button"
                        className="text-[#ff2a2a] text-xl hover:scale-125 transition-transform focus:outline-none"
                        onClick={handleBackward}
                      >
                        <FaBackward />
                      </button>
                      <button
                        type="button"
                        className="text-[#ff2a2a] text-2xl hover:scale-125 transition-transform focus:outline-none"
                        onClick={togglePlay}
                      >
                        {isPlaying ? <FaPause /> : <FaPlay />}
                      </button>
                      <button
                        type="button"
                        className="text-[#ff2a2a] text-xl hover:scale-125 transition-transform focus:outline-none"
                        onClick={handleForward}
                      >
                        <FaForward />
                      </button>
                      {/* Volume Control */}
                      <div className="flex items-center ml-4">
                        <button
                          type="button"
                          className="text-[#ff2a2a] text-xl mr-2 focus:outline-none"
                          onClick={toggleMute}
                        >
                          {muted || volume === 0 ? (
                            <FaVolumeMute />
                          ) : (
                            <FaVolumeUp />
                          )}
                        </button>
                        <input
                          type="range"
                          min="0"
                          max="1"
                          step="0.01"
                          value={volume}
                          onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
                          className="w-20 h-1 accent-[#ff2a2a] bg-[#ff2a2a]/30 rounded-lg outline-none"
                        />
                      </div>
                    </div>
                  </motion.div>
                </motion.div>
              </div>
            </div>

            {/* Social Media & Settings Panel */}
            <div className="rounded-2xl p-6 flex flex-col items-center justify-self-end transform translate-x-40">
              {/* Clickable overlay video
              <a
                href="https://farmskins.com/ref-150k"
                target="_blank"
                rel="noopener noreferrer"
                className="z-[999]"
              >
                <video
                  src="/overlay.webm"
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="w-128 cursor-pointer"
                />
              </a> */}
              <h2 className="text-3xl font-bold mb-4 tracking-wider">
                SOCIAL MEDIA
              </h2>
              <div className="flex space-x-4 mb-6 text-3xl">
                {content.socialLinks.map((s) => (
                  <a
                    key={s.id}
                    href={s.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-red-500 transition-colors cursor-pointer"
                    title={s.label}
                  >
                    {getIconComponent(s.icon)}
                  </a>
                ))}
              </div>
              <div className="w-full grid grid-cols-2 gap-2 text-sm mb-4">
                {content.systemSpecs.map((spec) => (
                  <div key={spec.id} className="flex justify-between px-2 py-1">
                    <span className="text-red-500 font-semibold">
                      {spec.label}
                    </span>
                    <span className="text-white text-right">{spec.value}</span>
                  </div>
                ))}
              </div>
              <div className="w-full border-t border-[#3a2d5c] pt-4 mb-2">
                <h3 className="text-lg font-bold text-white mb-2">
                  CS2 SETTINGS
                </h3>
                <div className="flex flex-wrap gap-4 mb-2">
                  {content.cs2Settings.map((s) => (
                    <div key={s.id} className="flex flex-col items-center">
                      <span className="text-red-500 font-bold">{s.value}</span>
                      <span className="text-xs text-white">{s.label}</span>
                    </div>
                  ))}
                </div>
                <div className="flex flex-wrap gap-4 mb-2">
                  {content.viewmodel.map((s) => (
                    <div key={s.id} className="flex flex-col items-center">
                      <span className="text-red-500 font-bold">{s.value}</span>
                      <span className="text-xs text-white">{s.label}</span>
                    </div>
                  ))}
                </div>
                <div className="flex flex-wrap gap-4 mb-4">
                  {content.videoSettings.map((s) => (
                    <div key={s.id} className="flex flex-col items-center">
                      <span className="text-red-500 font-bold">{s.value}</span>
                      <span className="text-xs text-white">{s.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </main>

      </div>
    </div>
  );
};

export default Hero;